// src/context/FirebaseContext.js
// Single Source of Truth: Firebase Auth state + secure server-side validation

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions, DEFAULT_STORE_ID } from '../firebase/firebase';
import {
    adminLogin,
    adminRegister,
    adminLogout,
    adminResetPassword
} from '../firebase/admin-auth';

const FirebaseContext = createContext();

// Session timeout: 30 minutes for admin security (industry standard)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const SESSION_STORAGE_KEY = 'adminSessionStart';

export const FirebaseProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [storeInfo, setStoreInfo] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Session management - use ref for runtime, sessionStorage for persistence across refresh
    const sessionStartRef = useRef(null);
    const sessionCheckIntervalRef = useRef(null);

    // Check if session is still valid
    const isSessionValid = useCallback(() => {
        if (!sessionStartRef.current) return false;
        return (Date.now() - sessionStartRef.current) < SESSION_TIMEOUT_MS;
    }, []);

    // Start a new session
    const startSession = useCallback(() => {
        const now = Date.now();
        sessionStartRef.current = now;
        sessionStorage.setItem(SESSION_STORAGE_KEY, now.toString());
    }, []);

    // Clear all auth state
    const clearAuthState = useCallback(() => {
        setCurrentUser(null);
        setStoreInfo(null);
        setUserRole(null);
        sessionStartRef.current = null;
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        if (sessionCheckIntervalRef.current) {
            clearInterval(sessionCheckIntervalRef.current);
            sessionCheckIntervalRef.current = null;
        }
    }, []);

    // Validate store access via secure Cloud Function
    const validateAndSetStoreInfo = useCallback(async () => {
        try {
            const getEffectiveStoreIdFunc = httpsCallable(functions, 'getEffectiveStoreId');
            const result = await getEffectiveStoreIdFunc();

            if (!result.data.success || !result.data.storeId) {
                throw new Error('Store configuration not found');
            }

            const { storeId, storeName } = result.data;

            // Single-tenant check: verify store matches this deployment
            if (DEFAULT_STORE_ID && storeId !== DEFAULT_STORE_ID) {
                console.error('🚨 SECURITY: Store mismatch', { storeId, expected: DEFAULT_STORE_ID });
                throw new Error('Access denied: This admin panel is configured for a different store.');
            }

            setStoreInfo({ storeId, storeName });
            setUserRole('store_owner');
            return true;

        } catch (err) {
            console.error('Store validation failed:', err);
            setError(err.message);
            return false;
        }
    }, []);

    // Main auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    // User is signed in
                    setCurrentUser({ uid: user.uid, email: user.email });

                    // Check sessionStorage for persisted session (survives page refresh)
                    const storedSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
                    if (storedSession && !sessionStartRef.current) {
                        sessionStartRef.current = parseInt(storedSession, 10);
                    }

                    if (sessionStartRef.current && isSessionValid()) {
                        // Session exists and is valid - verify store access
                        const valid = await validateAndSetStoreInfo();
                        if (!valid) {
                            await adminLogout();
                            clearAuthState();
                        }
                    } else if (sessionStartRef.current && !isSessionValid()) {
                        // Session expired
                        console.log('Session expired, logging out');
                        await adminLogout();
                        clearAuthState();
                    } else {
                        // No session - user needs to go through login flow
                        // This handles: initial load before login, or cleared session
                        // Don't auto-logout here - let them stay on login page
                        setStoreInfo(null);
                        setUserRole(null);
                    }

                } else {
                    // User is signed out
                    clearAuthState();
                }
            } catch (err) {
                console.error('Auth state change error:', err);
                setError(err.message);
                clearAuthState();
            } finally {
                setLoading(false);
            }
        });

        return () => {
            unsubscribe();
            if (sessionCheckIntervalRef.current) {
                clearInterval(sessionCheckIntervalRef.current);
            }
        };
    }, [clearAuthState, isSessionValid, validateAndSetStoreInfo]);

    // Session timeout checker (runs every minute)
    useEffect(() => {
        if (currentUser && storeInfo) {
            sessionCheckIntervalRef.current = setInterval(() => {
                if (!isSessionValid()) {
                    console.log('Session timeout - logging out');
                    adminLogout().then(() => clearAuthState());
                }
            }, 60000); // Check every minute

            return () => {
                if (sessionCheckIntervalRef.current) {
                    clearInterval(sessionCheckIntervalRef.current);
                }
            };
        }
    }, [currentUser, storeInfo, isSessionValid, clearAuthState]);

    // Login wrapper - handles server validation and session start
    const login = async (email, password) => {
        try {
            setError(null);
            setLoading(true);

            // adminLogin validates via getEffectiveStoreId Cloud Function
            const result = await adminLogin(email, password);

            if (result.success) {
                // Start session timer (persists to sessionStorage)
                startSession();

                // Set state from validated response
                setCurrentUser({ uid: auth.currentUser.uid, email: auth.currentUser.email });
                setStoreInfo({ storeId: result.storeId, storeName: result.storeName });
                setUserRole('store_owner');

                // Single-tenant deployment check
                if (DEFAULT_STORE_ID && result.storeId !== DEFAULT_STORE_ID) {
                    await adminLogout();
                    clearAuthState();
                    throw new Error('Access denied: This admin panel is configured for a different store.');
                }
            }

            return result;

        } catch (err) {
            console.error('Login error:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Register wrapper
    const register = async (registrationData) => {
        try {
            setError(null);
            setLoading(true);

            const result = await adminRegister(registrationData);
            // Registration signs out user - they need to login
            return result;

        } catch (err) {
            console.error('Registration error:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Logout wrapper
    const logout = async () => {
        try {
            setLoading(true);
            setError(null);

            await adminLogout();
            clearAuthState();

            return { success: true };

        } catch (err) {
            console.error('Logout error:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Reset password wrapper
    const resetPassword = async (email) => {
        try {
            setLoading(true);
            setError(null);
            return await adminResetPassword(email);
        } catch (err) {
            console.error('Reset password error:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Helper: Check if fully authenticated (user + store validated)
    const isAuthenticated = useCallback(() => {
        return !!(currentUser && storeInfo && isSessionValid());
    }, [currentUser, storeInfo, isSessionValid]);

    const value = {
        currentUser,
        storeInfo,
        userRole,
        loading,
        error,
        // Auth functions
        login,
        register,
        logout,
        resetPassword,
        // Helpers
        isAuthenticated,
        // Legacy support - alias for isAuthenticated
        isAuthenticatedAdmin: isAuthenticated,
        authInitialized: !loading
    };

    return (
        <FirebaseContext.Provider value={value}>
            {children}
        </FirebaseContext.Provider>
    );
};

export const useFirebase = () => {
    const context = useContext(FirebaseContext);
    if (!context) {
        throw new Error('useFirebase must be used within a FirebaseProvider');
    }
    return context;
};

export default FirebaseProvider;